'use client';

import { useFrame } from '@react-three/fiber';
import { useEffect, useMemo, useRef } from 'react';
import { Color, type Mesh } from 'three';
import { uniform } from 'three/tsl';
import { AdditiveBlending, DoubleSide, MeshBasicNodeMaterial } from 'three/webgpu';
import { createPowerRingColorNode } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/backdrop/power-ring/powerRingShader';
import { type PowerRingProps } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/backdrop/power-ring/types';
import { NEAR_ZERO_3 } from '@/app/lcv/components/sections/spartan/shared/webgpu/constants';

// The power ring: a field of filaments and concentric rings on a single
// quad, lying flat on the dot grid.
//
// IT LIES ON THE GROUND, not behind anything. Mounted as a child of
// DotGridGround's mesh it inherits that plane's pose exactly, so the two
// stay welded together however the floor is re-aimed, with no second copy
// of the ground's position and rake to keep in agreement.
//
// WHAT CAME OUT. The original was posed beat by beat through an intro
// sequence, retargeted mid-run into a speedometer dial, and pushed around
// by a drag rig while the user turned an engine. None of those callers
// exist any more. What is left is the field itself and the two dials a
// backdrop actually needs: a master fade, and a colour it can be driven
// toward.

export const PowerRing = ({
  size,
  offsetAlongNormal,
  zoom,
  flowSpeed,
  noiseScale,
  warpStrength,
  swirlSpeed,
  ringFrequency,
  ringScale,
  ringPulseSpeed,
  ringSharpness,
  brightness,
  baseColor,
  visibilityRef,
  warmUp = false,
  colorRef,
  poseRef,
  gamma,
  coreOpacity,
  coreRadius,
  coreFeather,
  vignetteRadius,
  vignetteFeather,
  opacityScale,
  trailLength,
  trailDecay,
  spin = 0,
  octaves,
  trailSamples,
  isAnimating = true,
  timeScale = 1,
  resetKey = 0,
  renderOrder = 0,
  breathMin = 1,
  breathMax = 1,
  breathSeconds = 0,
  breathPeakAt = 0,
  breathGammaPeak
}: PowerRingProps) => {
  // Created once and mutated below, never rebuilt -- rebuilding would
  // detach them from the compiled material.
  /* eslint-disable react-hooks/exhaustive-deps */
  const timeUniform = useMemo(() => uniform(0), []);
  const zoomUniform = useMemo(() => uniform(zoom), []);
  const flowSpeedUniform = useMemo(() => uniform(flowSpeed), []);
  const noiseScaleUniform = useMemo(() => uniform(noiseScale), []);
  const warpStrengthUniform = useMemo(() => uniform(warpStrength), []);
  const swirlSpeedUniform = useMemo(() => uniform(swirlSpeed), []);
  const ringFrequencyUniform = useMemo(() => uniform(ringFrequency), []);
  const ringScaleUniform = useMemo(() => uniform(ringScale), []);
  const ringPulseSpeedUniform = useMemo(() => uniform(ringPulseSpeed), []);
  const ringSharpnessUniform = useMemo(() => uniform(ringSharpness), []);
  const brightnessUniform = useMemo(() => uniform(brightness), []);
  const gammaUniform = useMemo(() => uniform(gamma), []);
  const coreOpacityUniform = useMemo(() => uniform(coreOpacity), []);
  const coreRadiusUniform = useMemo(() => uniform(coreRadius), []);
  const coreFeatherUniform = useMemo(() => uniform(coreFeather), []);
  const vignetteRadiusUniform = useMemo(() => uniform(vignetteRadius), []);
  const vignetteFeatherUniform = useMemo(() => uniform(vignetteFeather), []);
  const opacityScaleUniform = useMemo(() => uniform(opacityScale), []);
  const trailLengthUniform = useMemo(() => uniform(trailLength), []);
  const trailDecayUniform = useMemo(() => uniform(trailDecay), []);
  // Both were the drag rig's inputs, and there is no drag rig any more.
  //
  // The ROTATION has been repurposed rather than retired: it is exactly
  // "how far is the field turned", which is what an authored spin wants,
  // so it is driven by `spin` / `RingPose.spin` now. The trail's velocity
  // stays at zero -- nothing smears the field sideways -- and is held as a
  // uniform rather than folded out so the node graph stays byte-identical
  // to the tuned original.
  const dragRotationUniform = useMemo(() => uniform(spin), []);
  const trailVelocityUniform = useMemo(() => uniform(0), []);
  const baseColorUniform = useMemo(() => uniform(new Color(baseColor)), []);
  // Kept as a Color object rather than rebuilt per frame, which would
  // allocate for no reason.
  const restColor = useMemo(() => new Color(baseColor), [baseColor]);
  /* eslint-enable react-hooks/exhaustive-deps */

  useEffect(() => {
    /* eslint-disable react-hooks/immutability */
    zoomUniform.value = zoom;
    flowSpeedUniform.value = flowSpeed;
    noiseScaleUniform.value = noiseScale;
    warpStrengthUniform.value = warpStrength;
    swirlSpeedUniform.value = swirlSpeed;
    ringFrequencyUniform.value = ringFrequency;
    ringScaleUniform.value = ringScale;
    ringPulseSpeedUniform.value = ringPulseSpeed;
    ringSharpnessUniform.value = ringSharpness;
    gammaUniform.value = gamma;
    coreOpacityUniform.value = coreOpacity;
    coreRadiusUniform.value = coreRadius;
    coreFeatherUniform.value = coreFeather;
    vignetteRadiusUniform.value = vignetteRadius;
    vignetteFeatherUniform.value = vignetteFeather;
    brightnessUniform.value = brightness;
    trailLengthUniform.value = trailLength;
    trailDecayUniform.value = trailDecay;
    /* eslint-enable react-hooks/immutability */
  });
  useEffect(() => {
    baseColorUniform.value.set(baseColor);
  }, [baseColor, baseColorUniform]);
  const material = useMemo(() => {
    const powerRingMaterial = new MeshBasicNodeMaterial();
    const {
      colorNode,
      opacityNode
    } = createPowerRingColorNode({
      time: timeUniform,
      zoom: zoomUniform,
      flowSpeed: flowSpeedUniform,
      noiseScale: noiseScaleUniform,
      warpStrength: warpStrengthUniform,
      swirlSpeed: swirlSpeedUniform,
      ringFrequency: ringFrequencyUniform,
      ringScale: ringScaleUniform,
      ringPulseSpeed: ringPulseSpeedUniform,
      ringSharpness: ringSharpnessUniform,
      brightness: brightnessUniform,
      baseColor: baseColorUniform,
      gamma: gammaUniform,
      coreOpacity: coreOpacityUniform,
      coreRadius: coreRadiusUniform,
      coreFeather: coreFeatherUniform,
      vignetteRadius: vignetteRadiusUniform,
      vignetteFeather: vignetteFeatherUniform,
      opacityScale: opacityScaleUniform,
      dragRotation: dragRotationUniform,
      trailVelocity: trailVelocityUniform,
      trailLength: trailLengthUniform,
      trailDecay: trailDecayUniform,
      octaves,
      trailSamples
    });
    powerRingMaterial.colorNode = colorNode;
    powerRingMaterial.opacityNode = opacityNode;
    powerRingMaterial.transparent = true;
    powerRingMaterial.depthWrite = false;
    powerRingMaterial.side = DoubleSide;
    powerRingMaterial.blending = AdditiveBlending;
    return powerRingMaterial;
    // octaves and trailSamples intentionally included: both change the
    // node graph itself.
  }, [timeUniform, ringSharpnessUniform, vignetteRadiusUniform, zoomUniform, flowSpeedUniform, noiseScaleUniform, warpStrengthUniform, swirlSpeedUniform, ringFrequencyUniform, ringScaleUniform, ringPulseSpeedUniform, brightnessUniform, baseColorUniform, gammaUniform, coreOpacityUniform, coreRadiusUniform, coreFeatherUniform, vignetteFeatherUniform, opacityScaleUniform, dragRotationUniform, trailVelocityUniform, trailLengthUniform, trailDecayUniform, octaves, trailSamples]);
  useEffect(() => () => {
    material.dispose();
  }, [material]);
  const colorReadRef = useRef(colorRef);
  const poseReadRef = useRef(poseRef);
  // One write-ref per uniform the pose can move. Verbose, but the
  // alternative is writing the memoised uniforms directly from the frame
  // loop, which `react-hooks/immutability` forbids.
  const posedWriteRef = useRef({
    spin: dragRotationUniform,
    zoom: zoomUniform,
    flowSpeed: flowSpeedUniform,
    noiseScale: noiseScaleUniform,
    warpStrength: warpStrengthUniform,
    swirlSpeed: swirlSpeedUniform,
    ringFrequency: ringFrequencyUniform,
    ringPulseSpeed: ringPulseSpeedUniform,
    ringScale: ringScaleUniform,
    ringSharpness: ringSharpnessUniform,
    brightness: brightnessUniform,
    gamma: gammaUniform,
    coreOpacity: coreOpacityUniform,
    coreRadius: coreRadiusUniform,
    coreFeather: coreFeatherUniform,
    vignetteRadius: vignetteRadiusUniform,
    vignetteFeather: vignetteFeatherUniform,
    opacityScale: opacityScaleUniform,
    trailLength: trailLengthUniform,
    trailDecay: trailDecayUniform
  });
  const timeWriteRef = useRef(timeUniform);
  const brightnessWriteRef = useRef(brightnessUniform);
  // The frame loop writes through a stable React ref rather than touching
  // the memoised uniform directly -- the convention the rest of these
  // effects follow.
  const baseColorWriteRef = useRef(baseColorUniform);
  const opacityScaleWriteRef = useRef(opacityScaleUniform);
  const meshRef = useRef<Mesh>(null);
  useEffect(() => {
    timeWriteRef.current.value = 0;
  }, [resetKey]);
  useFrame((_state, delta) => {
    if (isAnimating) {
      // R3F can deliver a millisecond-sized delta when a queued frame lands
      // during frameloop="never". Feeding that into the noise clock destroys
      // float precision and turns the aura into blocks after popup resume.
      const frameDelta = Number.isFinite(delta) ? Math.min(Math.max(delta, 0), 0.05) : 0;
      timeWriteRef.current.value += frameDelta * timeScale;
    }

    // TAKEN VERBATIM WHEN PRESENT -- these are absolute values the caller
    // has already interpolated between its own two ends, not offsets to be
    // applied on top of the resting props.
    const pose = poseReadRef.current?.value;
    if (pose) {
      const written = posedWriteRef.current;
      written.spin.value = pose.spin;
      written.zoom.value = pose.zoom;
      written.flowSpeed.value = pose.flowSpeed;
      written.noiseScale.value = pose.noiseScale;
      written.warpStrength.value = pose.warpStrength;
      written.swirlSpeed.value = pose.swirlSpeed;
      written.ringFrequency.value = pose.ringFrequency;
      written.ringPulseSpeed.value = pose.ringPulseSpeed;
      written.ringScale.value = pose.ringScale;
      written.ringSharpness.value = pose.ringSharpness;
      written.brightness.value = pose.brightness;
      written.gamma.value = pose.gamma;
      written.coreOpacity.value = pose.coreOpacity;
      written.coreRadius.value = pose.coreRadius;
      written.coreFeather.value = pose.coreFeather;
      written.vignetteRadius.value = pose.vignetteRadius;
      written.vignetteFeather.value = pose.vignetteFeather;
      written.trailLength.value = pose.trailLength;
      written.trailDecay.value = pose.trailDecay;
    }

    // THE BREATHING, APPLIED AFTER THE POSE ON PURPOSE.
    //
    // The pose block above writes `brightness` from whatever the caller is
    // interpolating; writing the swell before it would be silently
    // overwritten on every frame a pose is present. Last write wins, so
    // this has to be last.
    //
    // Recomputed from the authored range each frame rather than nudged, so
    // it can never drift and switching it off returns exactly to
    // `brightness`.
    // Restore the resting (or posed) gamma every frame when leaving Story.
    const restingGamma = pose?.gamma ?? gamma;
    posedWriteRef.current.gamma.value = restingGamma;
    if (breathSeconds > 0) {
      const loops = timeWriteRef.current.value / breathSeconds;
      // `0.5 - 0.5cos` rather than a raw sine: it runs 0..1 rather than
      // -1..1, and peaks exactly at `breathPeakAt` through the loop.
      const swell = 0.5 - 0.5 * Math.cos((loops - breathPeakAt + 0.5) * Math.PI * 2);
      brightnessWriteRef.current.value = breathMin + (breathMax - breathMin) * swell;
      if (breathGammaPeak !== undefined) {
        posedWriteRef.current.gamma.value = restingGamma + (breathGammaPeak - restingGamma) * swell;
      }
    }
    const posedColor = colorReadRef.current?.value;
    if (posedColor) {
      baseColorWriteRef.current.value.setRGB(posedColor.r, posedColor.g, posedColor.b);
    } else {
      baseColorWriteRef.current.value.copy(restColor);
    }

    // Recomputed from the prop each frame rather than nudged, so restoring
    // returns exactly to the authored opacity with no drift.
    const effectiveOpacity = (pose?.opacityScale ?? opacityScale) * (visibilityRef ? visibilityRef.value : 1);
    opacityScaleWriteRef.current.value = effectiveOpacity;

    // STOPS BEING DRAWN WHEN FADED OUT
    const isShown = warmUp || effectiveOpacity > NEAR_ZERO_3;
    if (meshRef.current) {
      meshRef.current.visible = isShown;
    }

    // REWIND THE CLOCK
    if (!isShown) {
      timeWriteRef.current.value = 0;
    }
  });
  return <mesh ref={meshRef} renderOrder={renderOrder} position={[0, 0, offsetAlongNormal]} material={material} frustumCulled={false}>
      <planeGeometry args={[size, size]} />
    </mesh>;
};